import TuneIcon from "@mui/icons-material/Tune";
import { BlockCard, DonutChart, SelectPill } from "../primitives";
import { BlockBaseProps, Option, Slice } from "../types";
import ReadingMixBlockSkeleton from "./Skeleton";
import styles from "./style.module.scss";

interface ReadingMixBlockProps extends BlockBaseProps {
  slices: Slice[];
  /** Dimensions the mix can be sliced by: genre, language, age and so on. */
  dimensions: Option[];
  dimension: string;
  onDimensionChange: (key: string) => void;
  title?: string;
}

/** How reading splits across a chosen dimension: donut plus scrolling legend. */
function ReadingMixBlock({
  slices,
  dimensions,
  dimension,
  onDimensionChange,
  title = "Reading mix",
  loading,
  empty,
  className,
}: ReadingMixBlockProps) {
  if (loading) {
    return (
      <ReadingMixBlockSkeleton
        count={slices.length || 5}
        className={className}
      />
    );
  }

  const top = slices[0];

  return (
    <BlockCard
      empty={empty}
      title={title}
      className={className}
      actions={
        <SelectPill
          options={dimensions}
          value={dimension}
          onChange={onDimensionChange}
          icon={<TuneIcon />}
          ariaLabel="Reading mix dimension"
        />
      }
    >
      <div className={styles.body}>
        <DonutChart
          slices={slices}
          size={150}
          className={styles.donut}
          center={
            top && (
              <>
                <span className={styles.topShare}>{`${top.share}%`}</span>
                <span className={styles.topLabel}>{top.label}</span>
              </>
            )
          }
        />

        <div className={styles.legend}>
          {slices.map((slice) => (
            <div key={slice.label} className={styles.legendRow}>
              <span className={styles.swatch} style={{ background: slice.color }} />
              <span className={styles.legendLabel}>{slice.label}</span>
              <span className={styles.legendShare}>{`${slice.share}%`}</span>
              {slice.delta && (
                <span
                  className={styles.legendDelta}
                  data-tone={slice.delta.tone}
                >
                  {slice.delta.value}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </BlockCard>
  );
}

export default ReadingMixBlock;
